import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Banknote,
  MapPin,
  ShoppingBag,
  Smartphone,
  Tag,
  X,
} from 'lucide-react';
import { PaymentMethod, PLATFORM_DEFAULTS } from '@kick/types';
import { getErrorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth-store';
import { subtotalCarrito, useCarrito } from '@/lib/carrito';
import { cn, formatCOP, formatDistance } from '@/lib/cn';
import { useCotizarPedido, useCrearPedido, useDirecciones } from '@/lib/queries';
import { useUbicacion } from '@/lib/ubicacion';
import { Boton, Campo, Cargando, Entrada, Vacio } from '@/components/ui';
import { useAvisos } from '@/components/avisos';

const METODOS = [
  {
    valor: PaymentMethod.CASH,
    texto: 'Efectivo',
    icono: Banknote,
    nota: 'Pagas al domiciliario cuando llegue',
  },
  {
    valor: PaymentMethod.TRANSFER,
    texto: 'Transferencia',
    icono: Smartphone,
    nota: 'Nequi, Daviplata o cuenta de banco',
  },
] as const;

export function Checkout() {
  const navegar = useNavigate();
  const avisos = useAvisos();
  const accessToken = useAuth((s) => s.accessToken);

  const comercio = useCarrito((s) => s.comercio);
  const lineas = useCarrito((s) => s.lineas);
  const vaciar = useCarrito((s) => s.vaciar);
  const fijarUbicacion = useUbicacion((s) => s.fijar);
  const ubicacion = useUbicacion((s) => s.ubicacion);

  const direcciones = useDirecciones(!!accessToken);
  const crear = useCrearPedido();

  const [addressId, setAddressId] = useState<string | undefined>(ubicacion?.addressId ?? undefined);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(PaymentMethod.CASH);
  const [tip, setTip] = useState(0);
  const [notas, setNotas] = useState('');
  const [cupon, setCupon] = useState('');
  const [cuponAplicado, setCuponAplicado] = useState<string | undefined>();

  /** Al servidor solo viajan identificadores: los precios los pone él. */
  const items = useMemo(
    () =>
      lineas.map((l) => ({
        productId: l.productId,
        quantity: l.quantity,
        optionIds: l.opciones.map((o) => o.optionId),
        ...(l.notas ? { notes: l.notas } : {}),
      })),
    [lineas],
  );

  // Preselección: la dirección que el cliente ya venía usando, o la
  // predeterminada. Sin dirección no hay distancia, y sin distancia no hay
  // cotización que mostrar.
  useEffect(() => {
    const lista = direcciones.data;
    if (!lista || lista.length === 0) return;
    setAddressId((actual) =>
      actual && lista.some((d) => d.id === actual)
        ? actual
        : (
            lista.find((d) => d.id === ubicacion?.addressId) ??
            lista.find((d) => d.isDefault) ??
            lista[0]!
          ).id,
    );
  }, [direcciones.data, ubicacion?.addressId]);

  const cotizacion = useCotizarPedido({
    merchantId: comercio?.id,
    addressId,
    items,
    tip,
    couponCode: cuponAplicado,
  });

  // Sin sesión no hay a quién entregarle el pedido.
  if (!accessToken) return <Navigate to="/acceso" replace />;

  if (!comercio || lineas.length === 0) {
    return (
      <div className="mx-auto max-w-md py-8">
        <Vacio
          icono={<ShoppingBag className="h-6 w-6" />}
          titulo="Tu carrito está vacío"
          descripcion="Agrega productos de un comercio para hacer un pedido."
          accion={
            <Link to="/">
              <Boton>Ver comercios</Boton>
            </Link>
          }
        />
      </div>
    );
  }

  if (direcciones.isLoading) return <Cargando texto="Cargando tus datos…" />;

  const confirmar = (): void => {
    if (!cotizacion.data || !addressId) return;

    crear.mutate(
      {
        merchantId: comercio.id,
        addressId,
        items,
        paymentMethod,
        tip,
        ...(notas.trim() ? { customerNotes: notas.trim() } : {}),
        ...(cuponAplicado ? { couponCode: cuponAplicado } : {}),
        // El servidor rechaza el pedido si su cálculo no da este total.
        expectedTotal: cotizacion.data.total,
      },
      {
        onSuccess: (pedido) => {
          vaciar();
          avisos.exito(`Pedido ${pedido.code} confirmado`);
          navegar(`/pedidos/${pedido.id}`, { replace: true });
        },
        onError: (error) => avisos.error(getErrorMessage(error)),
      },
    );
  };

  const subtotalLocal = subtotalCarrito(lineas);
  const sinDirecciones = direcciones.data?.length === 0;
  const puedeConfirmar = !!addressId && !!cotizacion.data?.covered && cotizacion.data.meetsMinimum;

  return (
    <div className="mx-auto max-w-2xl pb-8">
      <Link
        to="/"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-kick-gray transition hover:text-kick-ink"
      >
        <ArrowLeft className="h-4 w-4" />
        Seguir comprando
      </Link>

      <h1 className="text-3xl text-kick-dark">Confirmar pedido</h1>
      <p className="mt-1 text-sm text-kick-gray">{comercio.name}</p>

      {/* Dirección de entrega */}
      <section className="card-kick mt-6 p-5">
        <h2 className="text-lg text-kick-dark">¿Dónde lo entregamos?</h2>

        {sinDirecciones ? (
          <div className="mt-3 rounded-kick bg-amber-50 p-4">
            <p className="text-sm text-amber-800">
              Todavía no tienes direcciones guardadas. Necesitamos una con su punto exacto en el
              mapa para calcular el domicilio.
            </p>
            <Link to="/mis-direcciones" className="mt-3 inline-block">
              <Boton icono={<MapPin className="h-4 w-4" />}>Agregar dirección</Boton>
            </Link>
          </div>
        ) : (
          <div className="mt-3 space-y-2">
            {direcciones.data?.map((direccion) => (
              <label
                key={direccion.id}
                className={cn(
                  'flex cursor-pointer items-start gap-3 rounded-kick border p-3.5 transition',
                  addressId === direccion.id
                    ? 'border-kick-red bg-kick-red/5'
                    : 'border-kick-gray/20 hover:border-kick-gray/40',
                )}
              >
                <input
                  type="radio"
                  name="direccion"
                  checked={addressId === direccion.id}
                  onChange={() => {
                    setAddressId(direccion.id);
                    // La vitrina y la entrega usan la misma dirección.
                    fijarUbicacion({
                      addressId: direccion.id,
                      label: direccion.label,
                      addressLine: direccion.addressLine,
                      location: direccion.location,
                    });
                  }}
                  className="mt-1 h-4 w-4 accent-kick-red"
                />
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold text-kick-ink">{direccion.label}</span>
                  <span className="block text-sm text-kick-gray">{direccion.addressLine}</span>
                  {direccion.details && (
                    <span className="block text-sm text-kick-gray">{direccion.details}</span>
                  )}
                </span>
              </label>
            ))}

            <Link
              to="/mis-direcciones"
              className="block py-1 text-sm font-semibold text-kick-red hover:underline"
            >
              + Agregar otra dirección
            </Link>
          </div>
        )}
      </section>

      {/* Forma de pago */}
      <section className="card-kick mt-4 p-5">
        <h2 className="text-lg text-kick-dark">¿Cómo pagas?</h2>

        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {METODOS.map(({ valor, texto, icono: Icono, nota }) => (
            <label
              key={valor}
              className={cn(
                'flex cursor-pointer flex-col items-center gap-1 rounded-kick border p-3 text-center transition',
                paymentMethod === valor
                  ? 'border-kick-red bg-kick-red/5'
                  : 'border-kick-gray/20 hover:border-kick-gray/40',
              )}
            >
              <input
                type="radio"
                name="pago"
                checked={paymentMethod === valor}
                onChange={() => setPaymentMethod(valor)}
                className="sr-only"
              />
              <Icono
                className={cn(
                  'h-5 w-5',
                  paymentMethod === valor ? 'text-kick-red' : 'text-kick-gray',
                )}
              />
              <span className="text-sm font-semibold text-kick-ink">{texto}</span>
              <span className="text-xs text-kick-gray">{nota}</span>
            </label>
          ))}
        </div>

        {paymentMethod === PaymentMethod.TRANSFER && (
          <div className="mt-3 rounded-kick bg-kick-light px-3 py-2.5 text-xs text-kick-gray">
            <p className="font-semibold text-kick-ink">Cómo funciona</p>
            <ol className="mt-1 list-inside list-decimal space-y-0.5">
              <li>Cuando se asigne el domiciliario verás su cuenta y su QR.</li>
              <li>Transfieres y subes la foto del comprobante desde tu pedido.</li>
              <li>Él lo verifica en su banco y te lo confirma.</li>
            </ol>
            <p className="mt-1.5">
              Si algo sale mal, puedes pagar en efectivo al recibir: nadie pierde el pedido.
            </p>
          </div>
        )}
      </section>

      {/* Propina */}
      <section className="card-kick mt-4 p-5">
        <h2 className="text-lg text-kick-dark">Propina al domiciliario</h2>
        <p className="text-sm text-kick-gray">Va completa para quien te lleva el pedido.</p>

        <div className="mt-3 flex flex-wrap gap-2">
          {PLATFORM_DEFAULTS.tipPresets.map((monto) => (
            <button
              key={monto}
              type="button"
              onClick={() => setTip(monto)}
              className={cn(
                'rounded-kick border px-4 py-2 text-sm font-semibold transition',
                tip === monto
                  ? 'border-kick-red bg-kick-red text-white'
                  : 'border-kick-gray/25 text-kick-ink hover:border-kick-gray/50',
              )}
            >
              {monto === 0 ? 'Sin propina' : formatCOP(monto)}
            </button>
          ))}
        </div>
      </section>

      {/* Cupón y notas */}
      <section className="card-kick mt-4 p-5">
        <Campo etiqueta="¿Tienes un cupón?">
          <div className="flex gap-2">
            <Entrada
              value={cupon}
              onChange={(e) => setCupon(e.target.value.toUpperCase())}
              placeholder="BIENVENIDO"
              disabled={!!cuponAplicado}
              className="flex-1"
            />
            {cuponAplicado ? (
              <Boton
                type="button"
                variante="secundario"
                icono={<X className="h-4 w-4" />}
                onClick={() => {
                  setCuponAplicado(undefined);
                  setCupon('');
                }}
              >
                Quitar
              </Boton>
            ) : (
              <Boton
                type="button"
                variante="secundario"
                disabled={!cupon.trim()}
                onClick={() => setCuponAplicado(cupon.trim())}
              >
                Aplicar
              </Boton>
            )}
          </div>
        </Campo>

        {cuponAplicado && cotizacion.data && !cotizacion.data.coupon && (
          <p className="mt-1.5 text-sm text-red-600">
            Ese cupón no se puede aplicar a este pedido.
          </p>
        )}

        {cotizacion.data?.coupon && (
          <p className="mt-1.5 flex items-center gap-1.5 text-sm text-emerald-700">
            <Tag className="h-4 w-4" />
            {cotizacion.data.coupon.description ?? cotizacion.data.coupon.code} ·{' '}
            {formatCOP(cotizacion.data.coupon.discount)} de descuento
          </p>
        )}

        <Campo
          etiqueta="Notas para el comercio"
          ayuda="Instrucciones de entrega, aclaraciones…"
          className="mt-4"
        >
          <Entrada
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            placeholder="El timbre no sirve, por favor llamar"
            maxLength={300}
          />
        </Campo>
      </section>

      {/* Resumen */}
      <section className="card-kick mt-4 p-5">
        <h2 className="text-lg text-kick-dark">Resumen</h2>

        <ul className="mt-3 space-y-2 border-b border-kick-gray/15 pb-3">
          {lineas.map((linea) => (
            <li key={linea.key} className="flex gap-2 text-sm">
              <span className="font-semibold text-kick-gray">{linea.quantity}×</span>
              <span className="min-w-0 flex-1">
                <span className="block text-kick-ink">{linea.name}</span>
                {linea.opciones.length > 0 && (
                  <span className="block text-xs text-kick-gray">
                    {linea.opciones.map((o) => o.optionName).join(' · ')}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>

        {cotizacion.isLoading && <Cargando texto="Calculando…" />}

        {cotizacion.isError && (
          <p className="mt-3 flex items-start gap-2 rounded-kick bg-red-50 px-3 py-2 text-sm text-red-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            {getErrorMessage(cotizacion.error)}
          </p>
        )}

        {cotizacion.data && (
          <>
            <dl className="mt-3 space-y-1.5 text-sm">
              <Fila etiqueta="Subtotal" valor={formatCOP(cotizacion.data.subtotal)} />
              <Fila
                etiqueta={`Domicilio (${formatDistance(cotizacion.data.distanceMeters)})`}
                valor={
                  !cotizacion.data.covered
                    ? 'No disponible'
                    : cotizacion.data.deliveryFee === 0
                      ? 'Gratis'
                      : formatCOP(cotizacion.data.deliveryFee)
                }
                resaltado={cotizacion.data.covered && cotizacion.data.deliveryFee === 0}
              />
              {/* Solo cuando llueve: apagada vale cero y no se muestra. */}
              {cotizacion.data.serviceFee > 0 && (
                <Fila etiqueta="Tarifa lluvia" valor={formatCOP(cotizacion.data.serviceFee)} />
              )}
              {cotizacion.data.tip > 0 && (
                <Fila etiqueta="Propina" valor={formatCOP(cotizacion.data.tip)} />
              )}
              {cotizacion.data.premiumDiscount > 0 && (
                <Fila
                  etiqueta={`Descuento premium (${cotizacion.data.premiumPercent}%)`}
                  valor={`− ${formatCOP(cotizacion.data.premiumDiscount)}`}
                />
              )}
              {/* Solo la parte del cupón: la premium ya tiene su propia línea. */}
              {cotizacion.data.discountTotal > cotizacion.data.premiumDiscount && (
                <Fila
                  etiqueta="Descuento"
                  valor={`− ${formatCOP(cotizacion.data.discountTotal - cotizacion.data.premiumDiscount)}`}
                  resaltado
                />
              )}
            </dl>

            <div className="mt-3 flex items-baseline justify-between border-t border-kick-gray/15 pt-3">
              <span className="font-semibold text-kick-ink">Total</span>
              <span className="text-2xl font-bold text-kick-dark">
                {formatCOP(cotizacion.data.total)}
              </span>
            </div>

            {!cotizacion.data.covered && (
              <Aviso texto="Esa dirección está fuera de nuestra zona de cobertura. Elige otra para continuar." />
            )}

            {!cotizacion.data.meetsMinimum && (
              <Aviso
                texto={`Te faltan ${formatCOP(cotizacion.data.minOrderAmount - cotizacion.data.subtotal)} para alcanzar el pedido mínimo de este comercio.`}
              />
            )}

            {/* El servidor revalida cada precio: si el carrito quedó viejo, manda el suyo. */}
            {cotizacion.data.subtotal !== subtotalLocal && (
              <Aviso texto="Algún precio cambió mientras comprabas. El total de arriba es el actualizado." />
            )}
          </>
        )}
      </section>

      <div className="sticky bottom-0 mt-4 bg-kick-light py-3">
        <Boton
          className="w-full py-3.5 text-base"
          disabled={!puedeConfirmar}
          cargando={crear.isPending}
          onClick={confirmar}
        >
          {cotizacion.data
            ? `Confirmar pedido · ${formatCOP(cotizacion.data.total)}`
            : 'Confirmar pedido'}
        </Boton>
      </div>
    </div>
  );
}

function Fila({
  etiqueta,
  valor,
  resaltado,
}: {
  etiqueta: string;
  valor: string;
  resaltado?: boolean;
}) {
  return (
    <div className="flex justify-between">
      <dt className="text-kick-gray">{etiqueta}</dt>
      <dd className={cn(resaltado ? 'font-semibold text-emerald-600' : 'text-kick-ink')}>
        {valor}
      </dd>
    </div>
  );
}

function Aviso({ texto }: { texto: string }) {
  return (
    <p className="mt-3 flex items-start gap-2 rounded-kick bg-amber-50 px-3 py-2 text-sm text-amber-800">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      {texto}
    </p>
  );
}
